/* ── Anchored pop-outs (WebPopover) ──────────────────────────────────────
   Web only. Imported after global.css and web-motion.css by
   src/app/_layout.web.tsx. The panel itself reuses `.tabbio-menu` (fixed,
   --menu-bg, --gem-menu-shadow, z 1000) and, on phones, WebMenu's bottom
   sheet (`.tabbio-menu-sheet` + `.tabbio-menu-sheet-scrim`). These rules add
   the pop-out layout: a fixed header, a scrolling body and a fixed footer.
   No entrance animation on desktop: web chrome opens instantly. */

.tabbio-menu.tabbio-popover {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 0;
  overflow: hidden;
  border-radius: 20px;
  outline: none;
}
.tabbio-menu.tabbio-popover.is-sheet {
  max-height: 80dvh;
  padding: 0 0 env(safe-area-inset-bottom);
  border-radius: 24px 24px 0 0;
}

.tabbio-popover-header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  min-height: 56px;
  padding-block: 8px;
  padding-inline: 20px 12px;
  border-bottom: 1px solid var(--input-border);
}
.tabbio-popover-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
}
.tabbio-popover-action,
.tabbio-popover-footer-link {
  flex-shrink: 0;
  min-height: 36px;
  padding: 0 12px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.tabbio-popover-action:hover,
.tabbio-popover-footer-link:hover {
  background: var(--hover);
}
.tabbio-popover-action:focus-visible,
.tabbio-popover-footer-link:focus-visible,
.tabbio-popover .notification-row-main:focus-visible {
  outline: 2px solid var(--text-primary);
  outline-offset: -2px;
}

.tabbio-popover-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.tabbio-popover-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 36px 24px;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}
.tabbio-popover-state strong {
  color: var(--text-primary);
  font-weight: 500;
}
.tabbio-popover-state p {
  margin: 0;
}
.tabbio-popover-list {
  display: flex;
  flex-direction: column;
  padding: 6px;
}

.tabbio-popover-footer {
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  padding: 8px;
  border-top: 1px solid var(--input-border);
}
.tabbio-popover-footer-link {
  width: 100%;
  min-height: 40px;
  border-radius: 12px;
}

/* Notification rows, compact: the full page's rows sized for a pop-out. */
.tabbio-popover .notification-row {
  border-bottom: 0;
  border-radius: 12px;
  overflow: hidden;
}
.tabbio-popover .notification-row + .notification-row {
  margin-top: 2px;
}
.tabbio-popover .notification-row-main {
  min-height: 72px;
  padding-block: 12px;
  padding-inline: 16px 4px;
}
.tabbio-popover .notification-avatar {
  width: 40px;
  height: 40px;
  font-size: 15px;
}
.tabbio-popover .notification-avatar img {
  width: 30px;
  height: 30px;
}
.tabbio-popover .notification-title,
.tabbio-popover .notification-story {
  font-size: 14px;
  line-height: 20px;
}
.tabbio-popover .notification-body {
  font-size: 13px;
  line-height: 18px;
}
.tabbio-popover .notification-aside {
  width: auto;
  padding-top: 13px;
  padding-inline-end: 12px;
}
.tabbio-popover .notification-unread-dot {
  inset-inline-start: 5px;
  top: 30px;
}

/* CV review pop-out (CvReviewPopover): a CV's languages, its latest saved
   versions and sharing, as rows. Section labels are 12/500 secondary text,
   sections are split by the same hairline as the header and footer. */
.tabbio-cvrp-heading {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
  padding-block: 6px;
}
.tabbio-cvrp-heading .tabbio-popover-title {
  display: -webkit-box;
  overflow: hidden;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.tabbio-cvrp-sub {
  margin: 0;
  overflow: hidden;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tabbio-cvrp-section + .tabbio-cvrp-section {
  border-top: 1px solid var(--input-border);
}
.tabbio-cvrp-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-block: 6px 0;
  padding-inline: 0 8px;
}
.tabbio-cvrp-label-row .tabbio-cvrp-label {
  padding-block: 0;
}
.tabbio-cvrp-label {
  margin: 0;
  padding: 12px 20px 0;
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
}
.tabbio-cvrp-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 52px;
  padding: 8px 10px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  text-align: start;
  cursor: pointer;
}
.tabbio-cvrp-row:hover {
  background: var(--hover);
}
.tabbio-cvrp-row.is-alert {
  background: var(--hover);
}
.tabbio-cvrp-row.is-alert:hover {
  background: var(--hover-strong);
}
.tabbio-cvrp-row:focus-visible {
  outline: 2px solid var(--text-primary);
  outline-offset: -2px;
}
.tabbio-cvrp-icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--hover);
  color: var(--text-secondary);
}
.tabbio-cvrp-row.is-alert .tabbio-cvrp-icon {
  background: var(--menu-bg);
  color: var(--text-primary);
}
.tabbio-cvrp-icon.is-code {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
}
.tabbio-cvrp-text {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
}
.tabbio-cvrp-title,
.tabbio-cvrp-meta {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tabbio-cvrp-title {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
.tabbio-cvrp-meta {
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 16px;
}
.tabbio-cvrp-row.is-alert .tabbio-cvrp-title,
.tabbio-cvrp-row.is-alert .tabbio-cvrp-meta {
  white-space: normal;
}
.tabbio-cvrp-trailing {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 4px;
  color: var(--text-tertiary);
  font-size: 12px;
  white-space: nowrap;
}
.tabbio-cvrp-chevron {
  flex-shrink: 0;
  color: var(--text-tertiary);
}
.tabbio-cvrp-loading {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 20px;
}
.tabbio-cvrp-note {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 0;
  padding: 8px 20px 4px;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 18px;
}
.tabbio-cvrp-section .tabbio-popover-list + .tabbio-popover-list {
  padding-top: 0;
}
